import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail } from "lucide-react";
import { LogoMark, LOGO_URL } from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/team", label: "Team" },
  { to: "/philanthropy", label: "Philanthropy" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

// Layout: Navbar + Footer + PublicLayout wrapper


export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-2xl border-b border-black/10 shadow-[0_2px_12px_rgba(0,0,0,0.06)]" : "bg-white/60 backdrop-blur-md border-b border-white/40"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
          <LogoMark size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) => `nav-link font-heading text-[13px] font-semibold tracking-tight ${isActive ? "active text-[#12355B]" : "text-[#0A1220] hover:text-[#12355B]"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {/* Certifications CTA removed from header per client spec */}
        </div>

        <button
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2 text-[#222629]"
          onClick={() => setOpen(o => !o)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden bg-white/97 backdrop-blur-2xl border-t border-black/5"
          >
            <div className="px-5 sm:px-6 py-6 flex flex-col gap-4">
              {LINKS.map(l => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) => `font-heading text-base font-medium ${isActive ? "text-[#12355B]" : "text-neutral-700"}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink to="/certifications" data-testid="nav-mobile-link-certifications" className="font-heading text-base font-medium text-neutral-700">
                Certifications
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#222629] text-neutral-300 relative overflow-hidden">
    <div className="grain absolute inset-0" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-4 gap-10 md:gap-12 relative">
      <div className="md:col-span-2">
        <div className="bg-white rounded-2xl p-3 md:p-4 inline-block mb-6">
          <img src={LOGO_URL} alt="R K STEEL" className="h-14 md:h-16 w-auto" />
        </div>
        <p className="text-sm text-neutral-400 max-w-md leading-relaxed">
          A trusted partner in global industrial procurement and recycling solutions for more than 70 years — sourcing responsibly from India and international markets while helping industries reduce waste, recover valuable resources and build a cleaner, more sustainable future.
        </p>
        <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[#B8823E]">Recycling Today, Building Tomorrow.</p>
        <div className="mt-6 flex flex-col gap-3">
          <a
            href="mailto:steel_rk@yahoo.com"
            data-testid="footer-email-link"
            className="inline-flex items-center gap-3 text-sm text-neutral-300 hover:text-[#B8823E] transition-colors duration-300 group w-fit"
          >
            <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#B8823E]/15 flex items-center justify-center transition-colors duration-300">
              <Mail size={16} />
            </span>
            <span className="font-heading">steel_rk@yahoo.com</span>
          </a>
          <a
            href="mailto:support@rksteel.co"
            data-testid="footer-email-support-link"
            className="inline-flex items-center gap-3 text-sm text-neutral-300 hover:text-[#B8823E] transition-colors duration-300 group w-fit"
          >
            <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#B8823E]/15 flex items-center justify-center transition-colors duration-300">
              <Mail size={16} />
            </span>
            <span className="font-heading">support@rksteel.co</span>
          </a>
        </div>
      </div>
      <div>
        <h4 className="font-heading text-white text-xs tracking-[0.2em] uppercase mb-5 font-semibold">Explore</h4>
        <ul className="space-y-2.5 text-sm">
          {LINKS.map(l => (
            <li key={l.to}>
              <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="hover:text-[#B8823E] transition-colors">{l.label}</Link>
            </li>
          ))}
          <li>
            <Link to="/certifications" data-testid="footer-link-certifications" className="hover:text-[#B8823E] transition-colors">Certifications</Link>
          </li>
        </ul>
      </div>
      <div>
        <h4 className="font-heading text-white text-xs tracking-[0.2em] uppercase mb-5 font-semibold">Contact</h4>
        <ul className="space-y-2.5 text-sm text-neutral-400">
          <li><a href="mailto:steel_rk@yahoo.com" className="hover:text-[#B8823E]">steel_rk@yahoo.com</a></li>
          <li><a href="mailto:support@rksteel.co" className="hover:text-[#B8823E]">support@rksteel.co</a></li>
          <li><a href="tel:+919535516260" className="hover:text-[#B8823E]">+91 95355 16260</a></li>
          <li><a href="tel:+919035858364" className="hover:text-[#B8823E]">+91 90358 58364</a></li>
          <li className="text-xs leading-relaxed pt-2">Ground Floor, No 29/3, 3rd Cross,<br />Mysore Road, Pipe Lane,<br />Kasturba Nagar,<br />Bengaluru — 560026</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-6">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex flex-col md:flex-row justify-between text-xs text-neutral-500 gap-2">
        <div>© {new Date().getFullYear()} R K STEEL. All rights reserved.</div>
        <div className="flex gap-6">
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-[#B8823E]">Admin Panel</Link>
          <span>Crafted with rigor, delivered with trust.</span>
        </div>
      </div>
    </div>
  </footer>
);

export const PublicLayout = ({ children }) => (
  <div className="App relative">
    <Navbar />
    <main className="pt-0">{children}</main>
    <Footer />
  </div>
);
